import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Where this tenant's users land, chosen while the tenant is being created.
 *
 * WHY IT IS HERE AND NOT IN SETUP. Nothing decided this at creation:
 * `tenants.default_app_code` was only ever written afterwards, by
 * `PUT /tenants/{id}/settings`, so every freshly-provisioned tenant reached the
 * Platform with no default and its shell fell back to whatever its own ordering
 * produced. The first person to log in — the customer's administrator — got the
 * arbitrary answer.
 *
 * WHY IT SITS AFTER THE CONFIGURATOR. The eligible applications ARE the
 * commercial selection. Asking this before the products are chosen would offer
 * a list that is about to change, or worse, let an operator pick something the
 * configuration then removes.
 *
 * TWO SERVER ANSWERS, NO THIRD OPINION
 * ------------------------------------
 * This component computes NO entitlements. It reads two server lists and shows
 * their intersection:
 *
 *   `GET  /api/admin/commercial/presentation` — which catalogue entries are
 *        APPLICATIONS at all, with their canonical labels. A place a user can
 *        land, as opposed to a capability (Forecasting) or a capacity (Managed
 *        Storage). Derived from `module_registry_service.ENTITLEMENTS`.
 *
 *   `POST /api/admin/tenants/commercial/validate` — which app codes THIS
 *        configuration resolves to. The same call, and the same projection,
 *        that `create_tenant` validates against.
 *
 * An intersection of two server answers is not a second derivation. Computing
 * "what does this tenant get" in the browser would be, and it is exactly how
 * the screen would come to offer something creation then refuses.
 *
 * AND THE UI IS NOT THE RULE. `admin_service.create_tenant` refuses an
 * unlicensed default application with a 400 whatever this screen shows. Hiding
 * a choice is a courtesy to the operator; it is not enforcement.
 */

/** CRM Console. Overridden by the server's own answer once it arrives. */
const FALLBACK_DEFAULT_APP = 'sales_console';
/** CRM Home. Same: a starting value, replaced by what the server serves. */
const FALLBACK_LANDING = 'home';

/**
 * Only the CRM Console has somewhere to land INSIDE it. Every other
 * application is the destination, so there is no second choice to make and the
 * selector is not merely disabled — it is absent, and the stored value is
 * cleared. The server enforces the same rule rather than trusting this.
 */
export const landingPageApplies = (appCode) => appCode === FALLBACK_DEFAULT_APP;

/**
 * The eligible applications: catalogue applications this configuration
 * entitles. Exported so it can be tested without a network.
 *
 * `resolvedAppCodes === null` means NOT YET KNOWN — the validation has not
 * answered, or could not. That is deliberately different from "resolved to
 * nothing": an unknown answer falls back to the applications every tenant
 * receives regardless of what was sold, which can never be wrong, rather than
 * to an empty picker that reads as "this tenant gets no applications".
 */
export const eligibleApplications = (candidates, resolvedAppCodes) => {
  if (!Array.isArray(candidates)) return [];
  if (resolvedAppCodes === null || resolvedAppCodes === undefined) {
    return candidates.filter((c) => c.included);
  }
  const resolved = new Set(resolvedAppCodes);
  return candidates.filter((c) => resolved.has(c.code));
};

export default function DefaultApplicationSection({
  commercial,
  value,
  onChange,
}) {
  const [candidates, setCandidates] = useState(null);
  const [serverDefaultApp, setServerDefaultApp] = useState(FALLBACK_DEFAULT_APP);
  const [landingPages, setLandingPages] = useState(null);
  const [serverDefaultLanding, setServerDefaultLanding] = useState(FALLBACK_LANDING);
  const [catalogueError, setCatalogueError] = useState(null);
  // Three states, not `codes || []`: "not asked yet", "asked and failed" and
  // "answered with nothing" are three different things, and flattening them is
  // how an unread list renders as an empty entitlement.
  const [resolvedAppCodes, setResolvedAppCodes] = useState(null);
  const [resolving, setResolving] = useState(false);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` }),
    [],
  );

  const appCode = value?.default_app_code || serverDefaultApp;
  const landingPage = value?.default_landing_page || serverDefaultLanding;

  // The two static catalogues, read once. Neither depends on the commercial
  // selection, so neither belongs in the effect below.
  useEffect(() => {
    (async () => {
      try {
        const [presentation, landing] = await Promise.all([
          axios.get(`${API}/api/admin/commercial/presentation`, { headers }),
          axios.get(`${API}/api/admin/crm-landing-pages`, { headers }),
        ]);
        setCandidates(presentation.data?.default_application_candidates || []);
        if (presentation.data?.default_application_code) {
          setServerDefaultApp(presentation.data.default_application_code);
        }
        setLandingPages(landing.data?.pages || []);
        if (landing.data?.default) setServerDefaultLanding(landing.data.default);
      } catch (err) {
        // An unreadable catalogue is NOT an empty one. `null` keeps the two
        // apart all the way to the render, which then says so instead of
        // showing a picker with nothing in it.
        setCatalogueError('Could not read the application catalogue.');
      }
    })();
  }, [headers]);

  // WHICH APPLICATIONS THIS CONFIGURATION ENTITLES — asked of the server that
  // decides it. The configurator asks the same endpoint for its own reasons and
  // keeps the answer to itself, so this is a second call rather than a second
  // computation; the endpoint is pure and writes nothing.
  //
  // Debounced, because `commercial` changes on every seat spinner click.
  const revalidate = useCallback(async (cfg) => {
    setResolving(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/tenants/commercial/validate`, cfg, { headers });
      setResolvedAppCodes((data?.entitlements || []).map((e) => e.app_code));
    } catch (err) {
      // Back to unknown, not to empty.
      setResolvedAppCodes(null);
    } finally {
      setResolving(false);
    }
  }, [headers]);

  useEffect(() => {
    const t = setTimeout(() => revalidate(commercial), 350);
    return () => clearTimeout(t);
  }, [commercial, revalidate]);

  const eligible = useMemo(
    () => eligibleApplications(candidates, resolvedAppCodes),
    [candidates, resolvedAppCodes],
  );

  // THE SELECTION FOLLOWS THE SALE. Removing a product must not leave the
  // tenant defaulted into an application it no longer has — the server would
  // refuse the creation, and the operator would be reading a refusal about a
  // control they last touched twenty minutes ago.
  useEffect(() => {
    if (!eligible.length) return;
    if (eligible.some((c) => c.code === appCode)) return;
    onChange?.({ default_app_code: serverDefaultApp, default_landing_page: serverDefaultLanding });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reacts to the eligible set only; `onChange` is a new closure each render
  }, [eligible, appCode, serverDefaultApp, serverDefaultLanding]);

  const chooseApp = (code) => {
    onChange?.({
      default_app_code: code,
      // CLEARED, not carried. A landing page is meaningless outside the CRM
      // Console, and a value nothing reads is one every later screen has to
      // explain. The server does this too rather than trusting the client.
      default_landing_page: landingPageApplies(code) ? landingPage : null,
    });
  };

  const chooseLanding = (token) => {
    onChange?.({ default_app_code: appCode, default_landing_page: token });
  };

  return (
    <div className="mt-6 border-t border-border pt-5" data-testid="default-application-section">
      <p className="text-sm font-semibold text-foreground">Default application</p>
      <p className="mt-0.5 text-xs text-muted-foreground">
        Where this tenant&apos;s users land when they sign in. Only applications the
        selected products entitle can be chosen; each user can change their own
        default afterwards.
      </p>

      {catalogueError ? (
        <p className="mt-3 text-xs text-warning-text" data-testid="default-app-catalogue-error">
          {catalogueError} The tenant will be created with CRM Console as its default.
        </p>
      ) : (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5"
              htmlFor="default-app-select"
            >
              Application
            </label>
            <select
              id="default-app-select"
              value={appCode}
              onChange={(e) => chooseApp(e.target.value)}
              disabled={!candidates}
              className="w-full h-9 rounded-md border border-input bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
              data-testid="default-app-select"
            >
              {eligible.map((c) => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-muted-foreground" data-testid="default-app-note">
              {resolvedAppCodes === null
                ? 'Showing the applications every tenant receives — the rest resolve from the products above.'
                : resolving
                  ? 'Re-reading the entitled applications…'
                  : `${eligible.length} application${eligible.length === 1 ? '' : 's'} from this configuration.`}
            </p>
          </div>

          {/* SHOWN ONLY FOR THE CRM CONSOLE, and absent rather than disabled
              for every other application: a disabled control invites the
              operator to work out why, and the answer — "Booking IS the
              destination" — is not something a greyed-out box can say. */}
          {landingPageApplies(appCode) && (
            <div data-testid="default-landing-field">
              <label
                className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5"
                htmlFor="default-landing-select"
              >
                Landing page
              </label>
              <select
                id="default-landing-select"
                value={landingPage}
                onChange={(e) => chooseLanding(e.target.value)}
                disabled={!landingPages}
                className="w-full h-9 rounded-md border border-input bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                data-testid="default-landing-select"
              >
                {(landingPages || []).map((p) => (
                  <option key={p.token} value={p.token}>{p.label}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                The CRM Console screen users open first. Read from the canonical
                Console navigation, so it can only name a destination that exists.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
